import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toHTML } from '@portabletext/to-html';
import { LocationMarkerIcon, CurrencyDollarIcon, ClockIcon, CalendarIcon, UsersIcon, LinkIcon, CheckCircleIcon } from '@heroicons/react/outline';
import JobsLayout, { INK } from './JobsLayout';
import ApplyModal, { appliedOn } from './ApplyModal';
import { JobCard } from './JobSearch';
import { LoadingBlock } from './Spinner';
import { APPLY_API, TURNSTILE_SITE_KEY, api, communityLabel, placePath, prettyDate, provinceName, setTitle, takeEmbedded } from './shared';

// /jobs/<id>/<slug> (GROWTH-004): one job, with native apply. The edge function serves the same
// content as HTML (for crawlers) and adds the JobPosting data, canonical URL and robots rules.

const APPLY_CONFIG = { api: APPLY_API, turnstileSiteKey: TURNSTILE_SITE_KEY };

function Fact({ icon: Icon, label, children }) {
  if (!children) return null;
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-slate-400" aria-hidden="true" />
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-sm text-slate-900">{children}</p>
      </div>
    </div>
  );
}

export default function JobPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const key = location.pathname + location.search;
  const [state, setState] = useState(() => {
    const data = takeEmbedded(key);
    return data && data.job ? { id, job: data.job, similar: data.similar || [], error: '', status: 200 } : { id: '', job: null, similar: [], error: '', status: 0 };
  });
  const [open, setOpen] = useState(false);
  const [applied, setApplied] = useState(() => appliedOn(id));
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (state.id === id) return undefined;
    let cancelled = false;
    setApplied(appliedOn(id));
    api({ name: 'job', id })
      .then((res) => !cancelled && setState({ id, job: res.job, similar: res.similar || [], error: '', status: 200 }))
      .catch((e) => !cancelled && setState({ id, job: null, similar: [], error: e.message, status: e.status || 500 }));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const job = state.job;
  // Keep the address bar on the canonical URL (title slug may have changed).
  useEffect(() => {
    if (job && job.path && location.pathname !== job.path) navigate(`${job.path}${location.search}`, { replace: true });
    if (job) setTitle(`${job.title}${job.company ? ` – ${job.company}` : ''} | CanadaJobLeads`);
    window.scrollTo(0, 0);
  }, [job]);

  const bodyHtml = useMemo(() => (job && job.body && job.body.length ? toHTML(job.body) : ''), [job]);
  const bioHtml = useMemo(() => (job && job.companyBio && job.companyBio.length ? toHTML(job.companyBio) : ''), [job]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(`https://www.canadajobleads.ca${job.path}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // clipboard unavailable: the address bar still has the link
    }
  }

  if (!job) {
    return (
      <JobsLayout>
        <main className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
          {state.status === 404 ? (
            <>
              <h1 className="font-[Poppins] text-2xl font-semibold">This job isn’t available</h1>
              <p className="mt-2 text-slate-600">It may have been removed by the employer.</p>
            </>
          ) : state.error ? (
            <p className="text-red-700">{state.error}</p>
          ) : (
            <LoadingBlock label="Loading job…" minHeight="min-h-[40vh]" />
          )}
          {(state.status === 404 || state.error) && (
            <Link to="/jobs" className="mt-6 inline-block rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white">
              Browse all jobs
            </Link>
          )}
        </main>
      </JobsLayout>
    );
  }

  const where = [job.city, provinceName(job.province)].filter(Boolean).join(', ');
  const applyButton = job.open ? (
    applied ? (
      <p className="inline-flex items-center gap-2 rounded-xl bg-green-50 px-4 py-3 text-sm font-semibold text-green-800">
        <CheckCircleIcon className="h-5 w-5" aria-hidden="true" /> You applied on {prettyDate(applied)}
      </p>
    ) : (
      <button type="button" onClick={() => setOpen(true)} className="w-full cursor-pointer rounded-xl bg-primary px-6 py-3 text-base font-semibold text-white shadow-lg shadow-primary/30 hover:bg-red-800 sm:w-auto">
        Apply now
      </button>
    )
  ) : (
    <p className="rounded-xl bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-600">Applications for this job have closed.</p>
  );

  return (
    <JobsLayout>
      <section className="text-white" style={{ backgroundColor: INK }}>
        <div className="mx-auto max-w-6xl px-4 pb-10 pt-6 sm:px-6">
          <p className="text-sm text-white/60">
            <Link to="/jobs" className="hover:text-white">
              Jobs
            </Link>
            {job.province && (
              <>
                {' › '}
                <Link to={placePath(job.province)} className="hover:text-white">
                  {provinceName(job.province)}
                </Link>
              </>
            )}
            {job.city && job.province && (
              <>
                {' › '}
                <Link to={placePath(job.province, job.city)} className="hover:text-white">
                  {job.city}
                </Link>
              </>
            )}
          </p>
          <h1 className="mt-3 font-[Poppins] text-3xl font-semibold tracking-tight sm:text-4xl">{job.title}</h1>
          <p className="mt-2 text-lg text-white/80">{[job.company, where].filter(Boolean).join(' · ')}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {job.open && <span className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-white">Direct apply</span>}
            {job.communities.map((c) => (
              <Link key={c} to={`/jobs?community=${c}`} className="rounded-full border border-white/25 px-3 py-1 text-xs text-white/80 hover:border-white/60">
                {communityLabel(c)}
              </Link>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {applyButton}
            <button type="button" onClick={copyLink} className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-white/25 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10">
              <LinkIcon className="h-4 w-4" aria-hidden="true" /> {copied ? 'Link copied' : 'Copy link'}
            </button>
          </div>
        </div>
      </section>

      <main className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <article className="min-w-0 break-words rounded-2xl border border-slate-200 bg-white p-5 sm:p-8">
          <h2 className="font-[Poppins] text-xl font-semibold">About the job</h2>
          {bodyHtml ? (
            <div className="cjl-prose mt-3" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
          ) : (
            <p className="mt-3 text-slate-600">The employer hasn’t added a description.</p>
          )}
          {bioHtml && (
            <>
              <h2 className="mt-8 font-[Poppins] text-xl font-semibold">About {job.company || 'the company'}</h2>
              <div className="cjl-prose mt-3" dangerouslySetInnerHTML={{ __html: bioHtml }} />
            </>
          )}
          {job.open && !applied && (
            <div className="mt-8 border-t border-slate-100 pt-6">
              <button type="button" onClick={() => setOpen(true)} className="w-full cursor-pointer rounded-xl bg-primary px-6 py-3 font-semibold text-white hover:bg-red-800 sm:w-auto">
                Apply for this job
              </button>
            </div>
          )}
        </article>

        <aside className="space-y-4">
          <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
            <Fact icon={CurrencyDollarIcon} label="Salary">
              {job.salary}
            </Fact>
            <Fact icon={LocationMarkerIcon} label="Location">
              {[job.location, where].filter(Boolean).join(', ')}
            </Fact>
            <Fact icon={ClockIcon} label="Job type">
              {job.category}
            </Fact>
            <Fact icon={UsersIcon} label="Openings">
              {job.vacancies}
            </Fact>
            <Fact icon={CalendarIcon} label="Posted on">
              {prettyDate(job.postedOn)}
            </Fact>
            <Fact icon={CalendarIcon} label={job.open ? 'Apply by' : 'Closed on'}>
              {prettyDate(job.postExpiry)}
            </Fact>
          </div>
          <p className="px-1 text-xs text-slate-500">
            Your application goes straight to the employer. Never pay to apply for a job.
          </p>
        </aside>
      </main>

      {state.similar.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="font-[Poppins] text-xl font-semibold">More jobs</h2>
          <ul className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            {state.similar.map((s) => (
              <JobCard key={s.id} job={s} />
            ))}
          </ul>
        </section>
      )}

      {/* Mobile: apply always within reach */}
      {job.open && !applied && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 p-3 backdrop-blur sm:hidden">
          <button type="button" onClick={() => setOpen(true)} className="w-full cursor-pointer rounded-xl bg-primary py-3 font-semibold text-white">
            Apply now
          </button>
        </div>
      )}

      <ApplyModal
        open={open}
        onClose={() => setOpen(false)}
        job={{ _id: job.id, title: job.title, companyName: job.company }}
        config={APPLY_CONFIG}
        onApplied={(at) => setApplied(at)}
      />
    </JobsLayout>
  );
}
